import type { CSSProperties } from "react";
import { FileText, Package, PieChart, ShoppingCart } from "lucide-react";
import { DonutChart } from "../../components/DonutChart";
import { BarChart } from "../../components/BarChart";
import { SectionToggleWrapper } from "../../components/SectionToggleWrapper";
import { CARD_BG, SHADOW } from "../../config/theme";
import {
  formatImpressions,
  formatRoasBadge,
  type AjinomotoCampaignRowViewModel,
  type AjinomotoProductCardViewModel,
  ROAS_THRESHOLDS,
} from "./ajinomotoGyozaLayoutData";
import type { SectionToggleControls } from "./AjinomotoGyozaSectionTypes";

const CARD_BACKGROUND_CLASSES = CARD_BG;

const productCardGridTemplate: CSSProperties = {
  gridTemplateAreas: '"title title roas" "meta . image" "sales units image"',
  gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr) auto",
};

interface AjinomotoSectionProps {
  sectionControls: SectionToggleControls;
}

interface AjinomotoProductComparisonSectionProps
  extends AjinomotoSectionProps {
  products: AjinomotoProductCardViewModel[];
}

interface AjinomotoEngagementSectionProps extends AjinomotoSectionProps {
  products: AjinomotoProductCardViewModel[];
  totalAddToCart: number;
  totalInStoreUnits: number;
  productColors: string[];
}

interface AjinomotoProductVisualizationsSectionProps
  extends AjinomotoSectionProps {
  products: AjinomotoProductCardViewModel[];
  productColors: string[];
  totalSales: string;
}

interface AjinomotoCampaignDetailsSectionProps extends AjinomotoSectionProps {
  campaignCount: number;
  campaigns: AjinomotoCampaignRowViewModel[];
}

const getRoasBadgeClasses = (roas: number): string => {
  if (roas >= ROAS_THRESHOLDS.EXCELLENT) {
    return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200";
  }
  if (roas >= ROAS_THRESHOLDS.GOOD) {
    return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200";
  }
  return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200";
};

export function AjinomotoProductComparisonSection({
  products,
  sectionControls,
}: AjinomotoProductComparisonSectionProps) {
  return (
    <SectionToggleWrapper
      sectionId="gyoza-product-comparison"
      title="Product Performance Comparison"
      hidden={sectionControls.isSectionHidden("gyoza-product-comparison")}
      onHide={sectionControls.hideSection}
      onRestore={sectionControls.restoreSection}
      className="mb-8"
    >
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
        <Package className="w-5 h-5" />
        Product Performance Comparison
      </h2>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {products.map((product) => (
          <div
            key={product.product_name}
            className={`${CARD_BACKGROUND_CLASSES} rounded-lg ${SHADOW.card} border border-gray-200 dark:border-gray-700 p-6`}
            style={{ borderColor: product.accentColor }}
          >
            <div className="grid gap-4 mb-6" style={productCardGridTemplate}>
              <div
                style={{ gridArea: "title" }}
                className="flex flex-col gap-1"
              >
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white leading-tight">
                  {product.product_name}
                </h3>
              </div>

              <div style={{ gridArea: "roas" }} className="justify-self-end">
                <span
                  className={`inline-flex items-center gap-1 whitespace-nowrap px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoasBadgeClasses(product.roasValue)}`}
                >
                  <span className="uppercase tracking-wide">ROAS</span>
                  <span className="font-bold">
                    {formatRoasBadge(product.roasValue)}
                  </span>
                </span>
              </div>

              <div
                style={{ gridArea: "meta" }}
                className="text-sm text-gray-600 dark:text-gray-400"
              >
                {product.campaign_count} campaign
                {product.campaign_count !== 1 ? "s" : ""}
              </div>

              <div style={{ gridArea: "sales" }}>
                <div className="flex flex-col gap-1 justify-end h-full">
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    Sales
                  </div>
                  <div className="text-2xl font-bold text-gray-900 dark:text-white leading-tight">
                    $
                    {product.totalSalesValue.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                    })}
                  </div>
                </div>
              </div>

              <div style={{ gridArea: "units" }}>
                <div className="flex flex-col gap-1 justify-end h-full">
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    Units
                  </div>
                  <div className="text-2xl font-bold text-gray-900 dark:text-white leading-tight">
                    {product.total_units.toLocaleString()}
                  </div>
                </div>
              </div>

              <div
                style={{ gridArea: "image" }}
                className="flex items-end justify-end"
              >
                <img
                  src={product.productImage}
                  alt={`${product.product_name} packaging`}
                  className="w-36 h-36 rounded-xl object-contain bg-white shadow-sm border border-gray-100"
                  loading="lazy"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <div>
                <div className="text-xs text-gray-600 dark:text-gray-400">
                  Add to Cart
                </div>
                <div className="text-sm font-semibold text-gray-900 dark:text-white">
                  {product.add_to_cart.toLocaleString()}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-500">
                  {product.addToCartRate.toFixed(1)}% CVR
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-600 dark:text-gray-400">
                  In-Store Units
                </div>
                <div className="text-sm font-semibold text-gray-900 dark:text-white">
                  {product.in_store_units.toLocaleString()}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-500">
                  $
                  {product.inStoreSalesValue.toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                  })}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-600 dark:text-gray-400">
                  CTR
                </div>
                <div className="text-sm font-semibold text-gray-900 dark:text-white">
                  {product.ctr.toFixed(3)}%
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-500">
                  {formatImpressions(product.total_impressions)} imp.
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </SectionToggleWrapper>
  );
}

export function AjinomotoEngagementSection({
  products,
  totalAddToCart,
  totalInStoreUnits,
  productColors,
  sectionControls,
}: AjinomotoEngagementSectionProps) {
  return (
    <SectionToggleWrapper
      sectionId="gyoza-engagement-metrics"
      title="Engagement & In-Store Performance"
      hidden={sectionControls.isSectionHidden("gyoza-engagement-metrics")}
      onHide={sectionControls.hideSection}
      onRestore={sectionControls.restoreSection}
      className="mb-8"
    >
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
        <ShoppingCart className="w-5 h-5" />
        Engagement & In-Store Performance
      </h2>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div
          className={`${CARD_BACKGROUND_CLASSES} rounded-lg ${SHADOW.card} border border-gray-200 dark:border-gray-700 p-6`}
        >
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
            Add-to-Cart Performance
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Total add-to-cart actions: {totalAddToCart.toLocaleString()}
          </p>
          <BarChart
            labels={products.map((product) => product.shortLabel)}
            datasets={[
              {
                label: "Add to Cart",
                data: products.map((product) => product.add_to_cart),
                backgroundColor: productColors,
              },
            ]}
            horizontal={true}
            showLegend={false}
            valueFormatter={(value) => Math.round(value).toLocaleString("en-US")}
          />
        </div>

        <div
          className={`${CARD_BACKGROUND_CLASSES} rounded-lg ${SHADOW.card} border border-gray-200 dark:border-gray-700 p-6`}
        >
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
            In-Store Performance
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Total in-store units: {totalInStoreUnits.toLocaleString()}
          </p>
          <BarChart
            labels={products.map((product) => product.shortLabel)}
            datasets={[
              {
                label: "In-Store Units",
                data: products.map((product) => product.in_store_units),
                backgroundColor: productColors,
              },
            ]}
            horizontal={true}
            showLegend={false}
            valueFormatter={(value) => Math.round(value).toLocaleString("en-US")}
          />
        </div>
      </div>
    </SectionToggleWrapper>
  );
}

export function AjinomotoProductVisualizationsSection({
  products,
  productColors,
  totalSales,
  sectionControls,
}: AjinomotoProductVisualizationsSectionProps) {
  return (
    <SectionToggleWrapper
      sectionId="gyoza-product-charts"
      title="Product Performance Visualizations"
      hidden={sectionControls.isSectionHidden("gyoza-product-charts")}
      onHide={sectionControls.hideSection}
      onRestore={sectionControls.restoreSection}
      className="mb-8"
    >
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
        <PieChart className="w-5 h-5" />
        Product Performance Visualizations
      </h2>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div
          className={`${CARD_BACKGROUND_CLASSES} rounded-lg ${SHADOW.card} border border-gray-200 dark:border-gray-700 p-6`}
        >
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            Sales Distribution by Product
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Total sales: $
            {parseFloat(totalSales).toLocaleString("en-US", {
              minimumFractionDigits: 2,
            })}
          </p>
          <DonutChart
            data={products.map((product) => ({
              brand_name: product.distributionLabel,
              units: product.totalSalesValue,
              percentage: (product.totalSalesValue / parseFloat(totalSales)) * 100,
            }))}
            colors={productColors}
          />
        </div>

        <div
          className={`${CARD_BACKGROUND_CLASSES} rounded-lg ${SHADOW.card} border border-gray-200 dark:border-gray-700 p-6`}
        >
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            ROAS by Product
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Target: {ROAS_THRESHOLDS.TARGET}x or above
          </p>
          <BarChart
            labels={products.map((product) => product.distributionLabel)}
            datasets={[
              {
                label: "ROAS",
                data: products.map((product) => product.roasValue),
                backgroundColor: productColors,
              },
            ]}
            horizontal={true}
            showLegend={false}
          />
        </div>
      </div>
    </SectionToggleWrapper>
  );
}

export function AjinomotoCampaignDetailsSection({
  campaignCount,
  campaigns,
  sectionControls,
}: AjinomotoCampaignDetailsSectionProps) {
  return (
    <SectionToggleWrapper
      sectionId="gyoza-campaign-details"
      title="Campaign Details"
      hidden={sectionControls.isSectionHidden("gyoza-campaign-details")}
      onHide={sectionControls.hideSection}
      onRestore={sectionControls.restoreSection}
      className="mb-8"
    >
      <div
        className={`${CARD_BACKGROUND_CLASSES} rounded-lg ${SHADOW.card} border border-gray-200 dark:border-gray-700 p-6`}
      >
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
          <FileText className="w-4 h-4" />
          Campaign Details
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          {campaignCount} active campaigns sorted by sales performance
        </p>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-[#403838]">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-[#B5A9AA] uppercase tracking-wider">
                  Campaign
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-[#B5A9AA] uppercase tracking-wider">
                  Product
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-[#B5A9AA] uppercase tracking-wider">
                  Sales
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-[#B5A9AA] uppercase tracking-wider">
                  Spend
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-[#B5A9AA] uppercase tracking-wider">
                  ROAS
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-[#B5A9AA] uppercase tracking-wider">
                  Units
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-[#B5A9AA] uppercase tracking-wider">
                  Days
                </th>
              </tr>
            </thead>
            <tbody
              className={`${CARD_BACKGROUND_CLASSES} divide-y divide-gray-200 dark:divide-gray-700`}
            >
              {campaigns.map((campaign) => (
                <tr
                  key={campaign.campaign_name}
                  className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <td
                    className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white max-w-xs truncate"
                    title={campaign.campaign_name}
                  >
                    {campaign.campaign_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                    {campaign.product || "—"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold text-gray-900 dark:text-white">
                    $
                    {campaign.totalSalesValue.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                    })}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-600 dark:text-gray-400">
                    $
                    {campaign.totalSpendValue.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                    })}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getRoasBadgeClasses(campaign.roasValue)}`}
                    >
                      {campaign.roasValue.toFixed(2)}x
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-600 dark:text-gray-400">
                    {campaign.total_units.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-600 dark:text-gray-400">
                    {campaign.days_active}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </SectionToggleWrapper>
  );
}
